import { useState, useCallback, useRef } from 'react'
import { useAiTextAssist } from '@hooks/useAiTextAssist'

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/** True when the AI result is already note HTML (e.g. filled template). */
export const isLikelyNoteHtml = (value: string): boolean => {
  const trimmed = value.trim()
  if (!trimmed.startsWith('<')) return false
  return /<\/?(?:p|div|ul|ol|li|h[1-6]|strong|span)\b/i.test(trimmed)
}

export const formatAiResponseForHtml = (response: string): string => {
  if (!response) return ''

  // Template-filled HTML should render as-is (do not escape / flatten).
  if (isLikelyNoteHtml(response)) {
    return response
  }

  let formatted = escapeHtml(response)

  formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')

  formatted = formatted.replace(/&lt;ul&gt;/g, '<ul class="ai-response-list">')
  formatted = formatted.replace(/&lt;\/ul&gt;/g, '</ul>')
  formatted = formatted.replace(/&lt;li&gt;/g, '<li>')
  formatted = formatted.replace(/&lt;\/li&gt;/g, '</li>')

  const lines = formatted.split('\n')
  const processedLines: string[] = []
  let inHtmlList = false
  let inBulletList = false
  let inNumberedList = false

  const numberedListPattern = /^(\d+)\.\s+(.+)$/
  const bulletListPattern = /^[-•*]\s+(.+)$/

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmedLine = line.trim()

    if (trimmedLine.includes('<ul') || trimmedLine.includes('<ol')) {
      inHtmlList = true
      processedLines.push(line)
      continue
    }
    if (trimmedLine.includes('</ul>') || trimmedLine.includes('</ol>')) {
      inHtmlList = false
      inBulletList = false
      inNumberedList = false
      processedLines.push(line)
      continue
    }

    if (inHtmlList) {
      processedLines.push(line)
      continue
    }

    const bulletMatch = trimmedLine.match(bulletListPattern)
    const numberedMatch = trimmedLine.match(numberedListPattern)

    if (bulletMatch) {
      if (inNumberedList) {
        processedLines.push('</ol>')
        inNumberedList = false
      }
      if (!inBulletList) {
        processedLines.push('<ul class="ai-response-list">')
        inBulletList = true
      }
      processedLines.push(`<li>${bulletMatch[1]}</li>`)
    } else if (numberedMatch) {
      if (inBulletList) {
        processedLines.push('</ul>')
        inBulletList = false
      }
      if (!inNumberedList) {
        processedLines.push('<ol class="ai-response-list">')
        inNumberedList = true
      }
      processedLines.push(`<li>${numberedMatch[2]}</li>`)
    } else {
      if (inBulletList) {
        processedLines.push('</ul>')
        inBulletList = false
      }
      if (inNumberedList) {
        processedLines.push('</ol>')
        inNumberedList = false
      }

      if (trimmedLine === '') {
        processedLines.push('')
      } else {
        processedLines.push(trimmedLine)
      }
    }
  }

  if (inBulletList) {
    processedLines.push('</ul>')
  }
  if (inNumberedList) {
    processedLines.push('</ol>')
  }

  // Keep paragraph / section breaks instead of collapsing into one line.
  const htmlParts: string[] = []
  for (let i = 0; i < processedLines.length; i++) {
    const part = processedLines[i]
    if (part === '') {
      if (htmlParts.length > 0 && htmlParts[htmlParts.length - 1] !== '<br>') {
        htmlParts.push('<br>')
      }
      continue
    }
    if (htmlParts.length > 0) {
      const prev = htmlParts[htmlParts.length - 1]
      const isBlock =
        part.startsWith('<ul') ||
        part.startsWith('<ol') ||
        part.startsWith('</ul') ||
        part.startsWith('</ol') ||
        prev.startsWith('<ul') ||
        prev.startsWith('<ol') ||
        prev.startsWith('</ul') ||
        prev.startsWith('</ol') ||
        prev.endsWith('</li>') ||
        prev === '<br>'
      if (!isBlock) {
        htmlParts.push('<br>')
      }
    }
    htmlParts.push(part)
  }

  return htmlParts.join('')
}

const TEMPLATE_BLANK_AFTER_LABEL_RE =
  /<(?:strong|b)[^>]*>\s*([^<:]+):\s*<\/(?:strong|b)>\s*(?:<span[^>]*>\s*_{3,}\s*<\/span>|_{3,})/gi

/** Patient and date are filled from the chart; the model must not rewrite them. */
const LOCKED_TEMPLATE_FIELD_LABELS = new Set(['patient', 'date'])

export type TemplateField = {
  label: string
  currentValue: string
  blank: boolean
}

const plainFieldValue = (html: string): string =>
  html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

const isUnderscoreBlank = (plain: string): boolean =>
  /^_+$/.test(plain.replace(/\s/g, ''))

/** Labels in template HTML that still have underscore placeholders. */
export const extractTemplateBlankLabels = (html: string): string[] => {
  const labels: string[] = []
  const seen = new Set<string>()
  TEMPLATE_BLANK_AFTER_LABEL_RE.lastIndex = 0
  let match: RegExpExecArray | null = TEMPLATE_BLANK_AFTER_LABEL_RE.exec(html)
  while (match) {
    const label = match[1].trim()
    if (label && !seen.has(label.toLowerCase())) {
      seen.add(label.toLowerCase())
      labels.push(label)
    }
    match = TEMPLATE_BLANK_AFTER_LABEL_RE.exec(html)
  }
  return labels
}

/** Labeled template fields the model may fill or revise. Skips Patient and Date. */
export const extractTemplateFields = (html: string): TemplateField[] => {
  const fields: TemplateField[] = []
  const seen = new Set<string>()

  const push = (label: string, rawValue: string): void => {
    const normalized = label.trim()
    const key = normalized.toLowerCase()
    if (!normalized || seen.has(key) || LOCKED_TEMPLATE_FIELD_LABELS.has(key)) {
      return
    }
    const currentValue = plainFieldValue(rawValue)
    const blank = currentValue === '' || isUnderscoreBlank(currentValue)
    seen.add(key)
    fields.push({
      label: normalized,
      currentValue: blank ? '' : currentValue,
      blank,
    })
  }

  const labelRe =
    /<(?:strong|b)[^>]*>\s*([^<:]+):\s*<\/(?:strong|b)>([\s\S]*?)(?=<(?:strong|b)\b|<\/li>|$)/gi
  let match: RegExpExecArray | null = labelRe.exec(html)
  while (match) {
    push(match[1], match[2] || '')
    match = labelRe.exec(html)
  }

  const notesMatch =
    /<div[^>]*>\s*Notes:\s*<\/div>\s*<div[^>]*>([\s\S]*?)<\/div>/i.exec(html)
  if (notesMatch) {
    push('Notes', notesMatch[1] || '')
  }

  return fields
}

/** Parse a JSON object of field fills from an AI completion. */
export const parseAiFieldFills = (content: string): Record<string, string> => {
  if (!content.trim()) return {}
  const fenced = content.match(/```(?:json)?\s*([\s\S]*?)```/i)
  const candidate = (fenced?.[1] || content).trim()
  const objectMatch = candidate.match(/\{[\s\S]*\}/)
  if (!objectMatch) return {}
  try {
    const parsed: unknown = JSON.parse(objectMatch[0])
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {}
    }
    const fills: Record<string, string> = {}
    Object.entries(parsed as Record<string, unknown>).forEach(
      ([key, value]) => {
        if (typeof value === 'string' && key.trim()) {
          fills[key.trim()] = value.trim()
        }
      },
    )
    return fills
  } catch {
    return {}
  }
}

/** Replace a blank or an existing value after a labeled field. */
export const applyTemplateFills = (
  html: string,
  fills: Record<string, string>,
): string => {
  let result = html
  Object.entries(fills).forEach(([label, value]) => {
    const trimmed = value.trim()
    if (
      !trimmed ||
      LOCKED_TEMPLATE_FIELD_LABELS.has(label.trim().toLowerCase())
    ) {
      return
    }
    const escapedValue = escapeHtml(trimmed).replace(/\n/g, '<br>')
    const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    if (label.trim().toLowerCase() === 'notes') {
      const notesDivRe =
        /(<div[^>]*>\s*Notes:\s*<\/div>\s*<div[^>]*>)([\s\S]*?)(<\/div>)/i
      if (notesDivRe.test(result)) {
        result = result.replace(
          notesDivRe,
          (_match, open: string, _inner: string, close: string) =>
            `${open}${escapedValue}${close}`,
        )
        return
      }
    }

    const fieldRe = new RegExp(
      `(<(?:strong|b)[^>]*>\\s*${escapedLabel}:\\s*</(?:strong|b)>)(\\s*)([\\s\\S]*?)(?=</li>|<(?:strong|b)\\b|$)`,
      'i',
    )
    if (!fieldRe.test(result)) return
    result = result.replace(
      fieldRe,
      (_match, open: string, space: string) => `${open}${space}${escapedValue}`,
    )
  })
  return result
}

export const NOTES_TEMPLATE_FILL_PROMPT_TEMPLATE = [
  'Update this dental note template from the sources provided (existing note draft and/or transcript).',
  'Fill every blank field.',
  'Update a field that already has a value, including Notes, only when the source adds or corrects it.',
  'Omit fields that should stay unchanged.',
  'Return ONLY a JSON object mapping exact field labels to string values.',
  'Example: {"Chief Complaint":"...","Notes":"..."}.',
  'Do not invent clinical details. When sources conflict, prefer the transcript.',
  'No markdown, no HTML, JSON only.',
].join(' ')

export const NOTES_TEMPLATE_FILL_SYSTEM_PROMPT = [
  "You are EnamelAI's dental-office note assistant.",
  'Fill blank fields and update existing clinical fields, including Notes, when the source adds or corrects them.',
  'Do not change Patient or Date. Return JSON only. Do not rewrite the template layout.',
].join(' ')

/** Convert note-template HTML into a markdown outline that preserves list structure. */
export const htmlTemplateToMarkdownOutline = (html: string): string => {
  if (!html.trim()) return ''

  let text = html
    .replace(/\r\n/g, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|tr)>/gi, '\n')
    .replace(/<li[^>]*>/gi, '\n- ')
    .replace(/<\/li>/gi, '')
    .replace(/<\/?(ul|ol)[^>]*>/gi, '\n')
    .replace(/<(strong|b)([^>]*)>/gi, '**')
    .replace(/<\/(strong|b)>/gi, '**')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

  // Normalize leftover bullet glyphs into markdown list markers.
  text = text
    .split('\n')
    .map((line) => {
      const trimmed = line.trim()
      if (/^[•*]\s+/.test(trimmed)) {
        return `- ${trimmed.replace(/^[•*]\s+/, '')}`
      }
      return trimmed
    })
    .filter(
      (line, index, lines) =>
        line !== '' || (index > 0 && lines[index - 1] !== ''),
    )
    .join('\n')

  return text.trim()
}

export const buildTemplateFillUserText = (
  body: string,
  opts: {
    fields: TemplateField[]
    structureHtml: string
    structure?: string
    tone?: string
  },
): string => {
  const parts: string[] = []
  if (opts.structure?.trim()) {
    parts.push(`Template name: ${opts.structure.trim()}`)
  }
  if (opts.tone?.trim()) {
    parts.push(`Tone: ${opts.tone.trim()}`)
  }
  const blanks = opts.fields.filter((field) => field.blank)
  const filled = opts.fields.filter((field) => !field.blank)
  if (blanks.length > 0) {
    parts.push('Blank fields to fill:')
    blanks.forEach((field) => {
      parts.push(`- ${field.label}`)
    })
    parts.push('')
  }
  if (filled.length > 0) {
    parts.push(
      'Fields you may update when the source adds or corrects the current value:',
    )
    filled.forEach((field) => {
      parts.push(`- ${field.label}: ${field.currentValue}`)
    })
    parts.push('')
  }
  parts.push('Template context (keep the layout; change only listed fields):')
  parts.push(htmlTemplateToMarkdownOutline(opts.structureHtml))
  parts.push('')
  parts.push(body)
  return parts.join('\n')
}

/** Strip HTML from note template content for AI structure prompts. */
export const stripHtmlToPlainText = (html: string): string => {
  return htmlTemplateToMarkdownOutline(html)
    .replace(/^- /gm, '')
    .replace(/\*\*/g, '')
    .trim()
}

export const buildNotesAiUserText = (
  body: string,
  opts?: { structure?: string; tone?: string; structureOutline?: string },
): string => {
  const parts: string[] = []
  if (opts?.structure?.trim()) {
    parts.push(`Structure: ${opts.structure.trim()}`)
  }
  if (opts?.structureOutline?.trim()) {
    parts.push(
      [
        'Fill this note template from the source. Keep the SAME visual structure:',
        '- Keep the title on its own line (use **bold** for the title).',
        '- Keep each field as its own markdown bullet line starting with "- ".',
        '- Keep every label (Patient, Date, Chief Complaint, etc.) and fill blank placeholders.',
        '- Update a filled field, including Notes, when the source adds or corrects it.',
        '- Keep the Notes section as a separate labeled block after the list.',
        '- Do NOT collapse the note into a single paragraph.',
        '',
        'Template:',
        opts.structureOutline.trim(),
      ].join('\n'),
    )
  }
  if (opts?.tone?.trim()) {
    parts.push(`Tone: ${opts.tone.trim()}`)
  }
  if (parts.length) {
    return `${parts.join('\n')}\n\n${body}`
  }
  return body
}

const ENHANCE_FEATURE_KEY_MAP: Record<string, string> = {
  simplify: 'simplify',
  summarize: 'summarize',
  enhance: 'formalize',
}

const buildAiNoteBodyHtml = (response: string): string => {
  const formattedResponse = formatAiResponseForHtml(response)
  return `<div data-ai-response="true">${formattedResponse}</div>`
}

/** Replace or append the inserted AI response block in note HTML. */
export const replaceInsertedAiResponseBlock = (
  noteHtml: string | null | undefined,
  aiResponse: string,
): string => {
  if (!aiResponse.trim()) return noteHtml ?? ''
  const nextBlock = buildAiNoteBodyHtml(aiResponse)
  const current = noteHtml ?? ''
  if (!current.trim()) return nextBlock

  if (typeof DOMParser === 'undefined') {
    if (/data-ai-response\s*=\s*["']true["']/i.test(current)) {
      return current.replace(
        /<div\b[^>]*\bdata-ai-response\s*=\s*["']true["'][^>]*>[\s\S]*?<\/div>/i,
        nextBlock,
      )
    }
    return `${current}<br>${nextBlock}`
  }

  const parser = new DOMParser()
  const doc = parser.parseFromString(
    `<div id="notes-ai-root">${current}</div>`,
    'text/html',
  )
  const root = doc.getElementById('notes-ai-root')
  if (!root) return `${current}<br>${nextBlock}`

  const existing = root.querySelector('[data-ai-response="true"]')
  const wrapper = doc.createElement('div')
  wrapper.innerHTML = nextBlock
  const nextNode = wrapper.firstElementChild
  if (!nextNode) return `${current}<br>${nextBlock}`

  if (existing) {
    existing.replaceWith(nextNode)
  } else {
    root.appendChild(doc.createElement('br'))
    root.appendChild(nextNode)
  }
  return root.innerHTML
}

export type GenerateResponseOptions = {
  label?: string
  featureKey?: string
  structure?: string
  /** Plain-text outline from a clinic note template (optional). */
  structureOutline?: string
  /** Original template HTML — when blanks exist, AI fills those in place. */
  structureHtml?: string
  tone?: string
  promptTemplate?: string
  systemPrompt?: string
}

export type AIResponseState = {
  aiResponse: string
  showAiActions: boolean
  showEnhanceDropdown: boolean
  currentQuery: string
  isLoading: boolean
  error: string | null
}

export type AIResponseActions = {
  setAiResponse: (response: string) => void
  setShowAiActions: (show: boolean) => void
  setShowEnhanceDropdown: (show: boolean) => void
  generateResponse: (
    query: string,
    options?: GenerateResponseOptions,
  ) => Promise<void>
  /** @deprecated Use generateResponse */
  generateMockResponse: (
    query: string,
    options?: GenerateResponseOptions,
  ) => Promise<void>
  regenerateResponse: () => Promise<void>
  enhanceResponse: (enhanceType: string) => Promise<void>
  insertResponse: (noteContent: string | null | undefined) => string
  replaceResponse: (noteContent: string | null | undefined) => string
  resetAIState: () => void
}

type LastAiRequest = {
  userText: string
  featureKey: string
  promptTemplate?: string
  systemPrompt?: string
  structureHtml?: string
}

export const useAIResponse = (): AIResponseState & AIResponseActions => {
  const { run, reset, isLoading, error } = useAiTextAssist()
  const [aiResponse, setAiResponse] = useState('')
  const [showAiActions, setShowAiActions] = useState(false)
  const [showEnhanceDropdown, setShowEnhanceDropdown] = useState(false)
  const [currentQuery, setCurrentQuery] = useState('')
  const lastAiRequestRef = useRef<LastAiRequest | null>(null)
  const requestIdRef = useRef(0)

  const startLoadingState = useCallback(
    (nextQuery?: string, options?: { label?: string }) => {
      setAiResponse('')
      setShowAiActions(true)
      if (nextQuery) {
        setCurrentQuery(options?.label || nextQuery)
      }
    },
    [],
  )

  const applyResult = useCallback((content: string | null): void => {
    if (!content) return
    setAiResponse(content)
  }, [])

  const runCompletion = useCallback(
    async (
      userText: string,
      featureKey: string,
      options?: Pick<
        GenerateResponseOptions,
        'promptTemplate' | 'systemPrompt' | 'structureHtml'
      >,
    ): Promise<void> => {
      lastAiRequestRef.current = {
        userText,
        featureKey,
        promptTemplate: options?.promptTemplate,
        systemPrompt: options?.systemPrompt,
        structureHtml: options?.structureHtml,
      }
      const requestId = ++requestIdRef.current
      const content = await run(userText, featureKey, {
        promptTemplate: options?.promptTemplate,
        systemPrompt: options?.systemPrompt,
      })
      if (requestId !== requestIdRef.current) return
      if (options?.structureHtml?.trim() && content) {
        const fills = parseAiFieldFills(content)
        if (Object.keys(fills).length > 0) {
          applyResult(applyTemplateFills(options.structureHtml, fills))
          return
        }
      }
      applyResult(content)
    },
    [applyResult, run],
  )

  const generateResponse = useCallback(
    async (query: string, options?: GenerateResponseOptions): Promise<void> => {
      const featureKey = options?.featureKey || 'custom'
      const structureHtml = options?.structureHtml?.trim()
      const templateFields = structureHtml
        ? extractTemplateFields(structureHtml)
        : []

      startLoadingState(query, options)

      if (structureHtml && templateFields.length > 0) {
        const userText = buildTemplateFillUserText(query, {
          fields: templateFields,
          structureHtml,
          structure: options?.structure,
          tone: options?.tone,
        })
        await runCompletion(userText, featureKey, {
          promptTemplate:
            options?.promptTemplate || NOTES_TEMPLATE_FILL_PROMPT_TEMPLATE,
          systemPrompt:
            options?.systemPrompt || NOTES_TEMPLATE_FILL_SYSTEM_PROMPT,
          structureHtml,
        })
        return
      }

      const userText = buildNotesAiUserText(query, {
        structure: options?.structure,
        structureOutline: options?.structureOutline,
        tone: options?.tone,
      })
      await runCompletion(userText, featureKey, {
        promptTemplate: options?.promptTemplate,
        systemPrompt: options?.systemPrompt,
      })
    },
    [runCompletion, startLoadingState],
  )

  const regenerateResponse = useCallback(async (): Promise<void> => {
    const lastRequest = lastAiRequestRef.current
    if (!lastRequest) return
    startLoadingState()
    await runCompletion(lastRequest.userText, lastRequest.featureKey, {
      promptTemplate: lastRequest.promptTemplate,
      systemPrompt: lastRequest.systemPrompt,
      structureHtml: lastRequest.structureHtml,
    })
  }, [runCompletion, startLoadingState])

  const enhanceResponse = useCallback(
    async (enhanceType: string): Promise<void> => {
      if (!aiResponse.trim()) return
      const featureKey = ENHANCE_FEATURE_KEY_MAP[enhanceType] || 'custom'
      startLoadingState()
      setShowEnhanceDropdown(false)
      await runCompletion(aiResponse, featureKey)
    },
    [aiResponse, runCompletion, startLoadingState],
  )

  const insertResponse = useCallback(
    (noteContent: string | null | undefined) => {
      if (!aiResponse) return noteContent ?? ''
      return replaceInsertedAiResponseBlock(noteContent, aiResponse)
    },
    [aiResponse],
  )

  const replaceResponse = useCallback(
    (noteContent: string | null | undefined) => {
      if (!aiResponse) return noteContent ?? ''
      return buildAiNoteBodyHtml(aiResponse)
    },
    [aiResponse],
  )

  const resetAIState = useCallback(() => {
    requestIdRef.current += 1
    lastAiRequestRef.current = null
    setAiResponse('')
    setCurrentQuery('')
    setShowAiActions(false)
    setShowEnhanceDropdown(false)
    reset()
  }, [reset])

  return {
    aiResponse,
    showAiActions,
    showEnhanceDropdown,
    currentQuery,
    isLoading,
    error,
    setAiResponse,
    setShowAiActions,
    setShowEnhanceDropdown,
    generateResponse,
    generateMockResponse: generateResponse,
    regenerateResponse,
    enhanceResponse,
    insertResponse,
    replaceResponse,
    resetAIState,
  }
}
